import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { TextButton, Button } from '@xing-com/button';
import { openUsercentricsSecondLayer } from '@xing-com/cookie-consent-usercentrics';
import type { UsercentricsObject } from '@xing-com/cookie-consent-usercentrics';
import {
  PAY_OR_CONSENT_STANDARD_UPSELL_HREF,
  PAY_OR_CONSENT_UPSELL_HREF,
} from '@xing-com/crate-upsell-utils';
import { useLoginAppUrl } from '@xing-com/crate-xinglet';
import { Divider } from '@xing-com/divider';
import { useExperiment } from '@xing-com/hub';
import { IconArrowDown, IconArrowUp } from '@xing-com/icons';
import { BodyCopy } from '@xing-com/typography';
import { XingLogo } from '@xing-com/xing-logo';

import * as Styled from './pay-or-consent.styled';
import { usePayOrConsentTracking } from './use-pay-or-consent-tracking';

const USERCENTRICS_CLOSING_EVENTS = new Set([
  'ACCEPT_ALL',
  'DENY_ALL',
  'SAVE',
  'CMP_CLOSED',
  'CMP_CLOSE',
  'CLOSE',
  'CLOSED',
]);
const USERCENTRICS_ACTION_TIMEOUT_MS = 5000;

const getUsercentricsObject = (): UsercentricsObject | undefined => {
  return window.__ucCmp as UsercentricsObject | undefined;
};

// Usercentrics calls can hang when the CMP script is blocked, so fail visibly.
const withUsercentricsTimeout = <T,>(
  promise: Promise<T>,
  timeoutMs = USERCENTRICS_ACTION_TIMEOUT_MS
): Promise<T> => {
  return new Promise<T>((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      reject(new Error('Usercentrics action timed out'));
    }, timeoutMs);

    promise.then(
      (value) => {
        clearTimeout(timeoutId);
        resolve(value);
      },
      (error: unknown) => {
        clearTimeout(timeoutId);
        reject(error);
      }
    );
  });
};

export const PayOrConsent: React.FC = () => {
  const isPubliftExperiment = useExperiment('ABACUS-555') === 'B';
  const isStandardUpsellExperiment = useExperiment('ABACUS-561') === 'B';
  const settingsId = isPubliftExperiment ? 'bKHOPD_JVewXgX' : undefined;

  const [accordionOpen, setAccordionOpen] = useState(false);
  const [isUsercentricsSecondLayerOpen, setIsUsercentricsSecondLayerOpen] =
    useState(false);
  const [usercentricsErrorBannerId, setUsercentricsErrorBannerId] = useState(0);
  const loginAppUrl = useLoginAppUrl();
  const {
    getTrackedLoginUrl,
    trackConsentAccept,
    trackGetPremium,
    trackInformationExpand,
    trackLogIn,
    trackXingPrivacy,
  } = usePayOrConsentTracking();

  const showUsercentricsError = (): void => {
    setUsercentricsErrorBannerId((id) => id + 1);
  };

  const handleOpenPartners = (
    event: React.MouseEvent<HTMLAnchorElement>
  ): void => {
    event.preventDefault();

    void withUsercentricsTimeout(openUsercentricsSecondLayer(settingsId))
      .then(() => {
        setIsUsercentricsSecondLayerOpen(true);
      })
      .catch(() => {
        setIsUsercentricsSecondLayerOpen(false);
        showUsercentricsError();
      });
  };

  const handleAcceptAll = (
    event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>
  ): void => {
    event.preventDefault();
    trackConsentAccept();

    const acceptAllConsents = getUsercentricsObject()?.acceptAllConsents;

    if (!acceptAllConsents) {
      showUsercentricsError();
      return;
    }

    void withUsercentricsTimeout(acceptAllConsents()).catch(() => {
      // If Usercentrics is blocked, consent remains false and the wall stays visible.
      showUsercentricsError();
    });
  };

  const handlePayClick = (): void => {
    trackGetPremium();
  };

  const handleLoginClick = (): void => {
    trackLogIn();
  };

  const handleToggleAccordion = (): void => {
    if (!accordionOpen) {
      trackInformationExpand();
    }

    setAccordionOpen(!accordionOpen);
  };

  const handlePrivacyClick = (): void => {
    trackXingPrivacy();
  };

  const loginUrl = getTrackedLoginUrl(loginAppUrl);
  const premiumHref = isStandardUpsellExperiment
    ? PAY_OR_CONSENT_STANDARD_UPSELL_HREF
    : PAY_OR_CONSENT_UPSELL_HREF;

  useEffect(() => {
    if (!isUsercentricsSecondLayerOpen) {
      return;
    }

    const handleCmpEvent = (event: Event): void => {
      const cmpEvent = event as CustomEvent<{ type?: string }>;

      if (
        cmpEvent.detail?.type &&
        USERCENTRICS_CLOSING_EVENTS.has(cmpEvent.detail.type)
      ) {
        setIsUsercentricsSecondLayerOpen(false);
      }
    };

    window.addEventListener('UC_UI_CMP_EVENT', handleCmpEvent);

    return () => {
      window.removeEventListener('UC_UI_CMP_EVENT', handleCmpEvent);
    };
  }, [isUsercentricsSecondLayerOpen]);

  if (isUsercentricsSecondLayerOpen) {
    return null;
  }

  return (
    <>
      <Styled.UsercentricsErrorBanner
        key={usercentricsErrorBannerId}
        display="fixed"
        handleOnClose={() => setUsercentricsErrorBannerId(0)}
        show={usercentricsErrorBannerId > 0}
        timeout={7000}
        variant="error"
      >
        <FormattedMessage
          id="PAY_OR_CONSENT_USERCENTRICS_BLOCKED_ERROR"
          values={{
            strong: (chunks) => <strong>{chunks}</strong>,
            br: () => <br />,
          }}
        />
      </Styled.UsercentricsErrorBanner>
      <Styled.PopUpWindow isShown={true}>
        <Styled.DialogContent>
          <Styled.Header>
            <XingLogo size={106} variant="duo" />
          </Styled.Header>
          <Styled.TopArea>
            <Styled.TopAreaHeader size="small">
              <FormattedMessage id="PAY_OR_CONSENT_TITLE" />
            </Styled.TopAreaHeader>
            <Styled.TopAreaBody size="medium">
              <FormattedMessage id="PAY_OR_CONSENT_DESCRIPTION" />
            </Styled.TopAreaBody>
            <Styled.PremiumButtonWrapper>
              <Styled.PremiumButton
                href={premiumHref}
                onClick={handlePayClick}
                variant="primary"
                size="medium"
              >
                <FormattedMessage id="PAY_OR_CONSENT_PREMIUM_CTA" />
              </Styled.PremiumButton>
            </Styled.PremiumButtonWrapper>
            <BodyCopy size="small" noMargin>
              <FormattedMessage
                id="PAY_OR_CONSENT_LOGIN"
                values={{
                  br: () => <br />,
                  a: (chunks) => (
                    <a href={loginUrl} onClick={handleLoginClick}>
                      {chunks}
                    </a>
                  ),
                }}
              />
            </BodyCopy>
          </Styled.TopArea>
          <Divider />
          <Styled.BottomArea>
            <BodyCopy size="small">
              <FormattedMessage
                id="PAY_OR_CONSENT_BOTTOM_MAIN"
                values={{
                  CMP: (chunks) => (
                    <Styled.Link href="#" onClick={handleOpenPartners}>
                      {chunks}
                    </Styled.Link>
                  ),
                }}
              />
            </BodyCopy>
            <TextButton
              size="small"
              onClick={handleToggleAccordion}
              icon={accordionOpen ? IconArrowUp : IconArrowDown}
              iconAfter={true}
            >
              <FormattedMessage id="PAY_OR_CONSENT_BOTTOM_COLLAPSABLE_TITLE" />
            </TextButton>
            <Styled.BottomAreaContent $open={accordionOpen}>
              <FormattedMessage
                id="PAY_OR_CONSENT_BOTTOM_COLLAPSABLE_CONTENT"
                values={{
                  P: (chunks) => (
                    <Styled.Paragraph size="small">{chunks}</Styled.Paragraph>
                  ),
                  Title: (chunks) => <Styled.Title>{chunks}</Styled.Title>,
                  CMP: (chunks) => (
                    <Styled.Link href="#" onClick={handleOpenPartners}>
                      {chunks}
                    </Styled.Link>
                  ),
                  Accept: (chunks) => <span>{chunks}</span>,
                  Divider: () => <Styled.Divider aria-hidden="true" />,
                }}
              />
            </Styled.BottomAreaContent>
          </Styled.BottomArea>
          <Styled.Footer>
            <TextButton
              size="small"
              fontWeight="regular"
              href="https://www.xing.com/legalnotice"
            >
              <FormattedMessage id="PAY_OR_CONSENT_FOOTER_IMPRESSUM" />
            </TextButton>
            <TextButton
              size="small"
              fontWeight="regular"
              href="https://privacy.xing.com/datenschutzerklaerung"
              onClick={handlePrivacyClick}
            >
              <FormattedMessage id="PAY_OR_CONSENT_FOOTER_PRIVACY" />
            </TextButton>
            <TextButton
              size="small"
              fontWeight="regular"
              href="https://www.xing.com/terms"
            >
              <FormattedMessage id="PAY_OR_CONSENT_FOOTER_TERMS" />
            </TextButton>
            <Button size="small" variant="tertiary" onClick={handleAcceptAll}>
              <FormattedMessage id="PAY_OR_CONSENT_FOOTER_ACCEPT_TRACKING" />
            </Button>
          </Styled.Footer>
        </Styled.DialogContent>
      </Styled.PopUpWindow>
    </>
  );
};
